import React from "react";
import { motion } from "framer-motion";
import {
  FaServer,
  FaDesktop,
  FaDatabase,
  FaUserLock,
  FaArrowRight,
  FaArrowDown,
  FaUsers,
  FaShieldAlt,
  FaClock,
  FaSync,
  FaMobile,
  FaCloud,
} from "react-icons/fa";

const HowWorks: React.FC = () => {
  const processSteps = [
    {
      step: 1,
      title: "You Interact",
      description: "Post, like, comment",
      icon: <FaUsers className="w-6 h-6" />,
      color: "from-blue-500 to-purple-600",
    },
    {
      step: 2,
      title: "Data Flows",
      description: "To Hedera Network",
      icon: <FaArrowRight className="w-6 h-6" />,
      color: "from-purple-600 to-pink-500",
    },
    {
      step: 3,
      title: "Secured Forever",
      description: "Immutable & transparent",
      icon: <FaShieldAlt className="w-6 h-6" />,
      color: "from-pink-500 to-orange-500",
    },
  ];

  const features = {
    backend: [
      {
        icon: <FaShieldAlt className="w-8 h-8" />,
        title: "Hedera Consensus Service",
        subtitle: "Think of it as a Digital Notary",
        description:
          "Keeps a secure and accurate record of all events and actions, like a diary that timestamps everything.",
        details: [
          "🔒 Every action is permanently recorded",
          "⏰ Exact timestamps for everything",
          "🌐 Distributed across multiple computers",
          "✅ Cannot be changed or deleted",
        ],
        analogy:
          "Like a bank's transaction ledger, but for social media - every post, like, and comment gets an official timestamp and can never be altered.",
      },
      {
        icon: <FaDatabase className="w-8 h-8" />,
        title: "Message Recording",
        subtitle: "Your Digital Memory Bank",
        description:
          "Records messages, likes, comments, and other activities on the Hedera network, ensuring everything is transparent and tamper-proof.",
        details: [
          "📝 All posts and interactions saved",
          "🔍 Completely transparent and viewable",
          "🛡️ Tamper-proof technology",
          "📊 Real-time activity tracking",
        ],
        analogy:
          "Imagine a library where every book (your posts) is automatically catalogued, protected, and can never be lost or changed without everyone knowing.",
      },
      {
        icon: <FaUserLock className="w-8 h-8" />,
        title: "Token Service",
        subtitle: "Your Digital Identity Card",
        description:
          "Creates unique digital profiles for users in the form of NFTs (Non-Fungible Tokens), like digital ID cards.",
        details: [
          "🆔 Unique digital identity for each user",
          "🎨 NFT-based profile system",
          "🔐 Secure authentication",
          "💎 Collectible and tradeable",
        ],
        analogy:
          "Like having a special passport that proves who you are online - unique, secure, and impossible to fake or duplicate.",
      },
    ],
    frontend: [
      {
        icon: <FaMobile className="w-8 h-8" />,
        title: "User Interface",
        subtitle: "Beautiful & Easy to Use",
        description:
          "Sleek and user-friendly design to make your experience smooth and enjoyable.",
        details: [
          "📱 Works perfectly on phones & computers",
          "🎨 Beautiful, modern design",
          "⚡ Fast and responsive",
          "🧭 Easy navigation for everyone",
        ],
        analogy:
          "Like having a well-organized, beautiful app that feels as natural as using your favorite social media platform.",
      },
      {
        icon: <FaSync className="w-8 h-8" />,
        title: "Smart Features",
        subtitle: "Connects You to Hedera",
        description:
          "Efficiently reads and writes data to Hedera, ensuring seamless real-time interactions.",
        details: [
          "🔄 Real-time data synchronization",
          "⚡ Lightning-fast interactions",
          "🔗 Direct connection to blockchain",
          "🎯 Smart error handling",
        ],
        analogy:
          "Like having a super-fast translator that instantly converts your actions into blockchain language and back to human-friendly format.",
      },
      {
        icon: <FaClock className="w-8 h-8" />,
        title: "Live Updates",
        subtitle: "Always Fresh & Current",
        description:
          "Continuously reads data from Hedera network to provide you with the most up-to-date messages and interactions.",
        details: [
          "🔄 Automatic updates every few seconds",
          "📡 Live data streaming",
          "🔔 Instant notifications",
          "📈 Real-time activity feeds",
        ],
        analogy:
          "Like having a news feed that updates itself automatically - you always see the latest posts and interactions.",
      },
    ],
  };

  return (
    <div className="relative w-full py-32 overflow-hidden bg-gradient-to-b from-[#FDF8F3] to-gray-50">
      {/* Background Elements */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 0.1 }}
        transition={{ duration: 2 }}
        className="absolute inset-0"
        style={{
          backgroundImage:
            "radial-gradient(circle at 50% 50%, #FF6B6B 1px, transparent 1px)",
          backgroundSize: "50px 50px",
        }}
      />

      {/* Floating Elements */}
      <motion.div
        animate={{ y: [0, -20, 0] }}
        transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
        className="absolute left-10 top-32 w-32 h-32 bg-gradient-to-r from-orange-400/30 to-yellow-400/30 rounded-full blur-xl"
      />
      <motion.div
        animate={{ y: [0, 20, 0] }}
        transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
        className="absolute right-10 bottom-32 w-32 h-32 bg-gradient-to-r from-teal-400/30 to-blue-400/30 rounded-full blur-xl"
      />

      <div className="relative container mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-purple-600 to-orange-500 mb-6">
            How does iBird work?
          </h2>
          <p className="text-lg sm:text-xl lg:text-2xl text-gray-600 max-w-3xl mx-auto mb-12">
            A powerful combination of Hedera technology and modern web
            development
          </p>

          {/* Simple Process Flow */}
          <div className="flex flex-col sm:flex-row items-center justify-center space-y-6 sm:space-y-0 sm:space-x-8 mb-12">
            {processSteps.map((step, index) => (
              <React.Fragment key={step.step}>
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5, delay: index * 0.2 }}
                  viewport={{ once: true }}
                  className="flex flex-col items-center"
                >
                  <div
                    className={`w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-gradient-to-r ${step.color} flex items-center justify-center text-white shadow-lg mb-3`}
                  >
                    <div className="text-xl sm:text-2xl font-bold">
                      {step.step}
                    </div>
                  </div>
                  <div className="text-center">
                    <h3 className="text-sm sm:text-base font-semibold text-gray-800 mb-1">
                      {step.title}
                    </h3>
                    <p className="text-xs sm:text-sm text-gray-600">
                      {step.description}
                    </p>
                  </div>
                </motion.div>

                {index < processSteps.length - 1 && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    transition={{ duration: 0.5, delay: (index + 1) * 0.2 }}
                    viewport={{ once: true }}
                    className="hidden sm:block"
                  >
                    <FaArrowRight className="text-2xl text-gray-400" />
                  </motion.div>
                )}

                {index < processSteps.length - 1 && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    transition={{ duration: 0.5, delay: (index + 1) * 0.2 }}
                    viewport={{ once: true }}
                    className="block sm:hidden"
                  >
                    <FaArrowDown className="text-2xl text-gray-400" />
                  </motion.div>
                )}
              </React.Fragment>
            ))}
          </div>

          {/* Explanation Text */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            viewport={{ once: true }}
            className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-2xl p-6 sm:p-8 max-w-4xl mx-auto"
          >
            <p className="text-base sm:text-lg text-gray-700 leading-relaxed">
              <span className="font-semibold text-purple-600">
                In simple terms:
              </span>{" "}
              When you use iBird, every action you take gets securely recorded
              on the Hedera blockchain network. Think of it like having a
              super-secure, permanent diary that everyone can read but no one
              can change or fake.
            </p>
          </motion.div>
        </motion.div>

        <div className="grid gap-12 lg:grid-cols-2">
          {/* Backend Section */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="bg-white/70 backdrop-blur-sm rounded-3xl p-6 sm:p-10 shadow-xl hover:shadow-2xl transition-all duration-500"
          >
            <div className="mb-6 sm:mb-8">
              <h3 className="text-3xl sm:text-4xl font-bold text-gray-800 mb-4 flex items-center justify-center sm:justify-start">
                <FaServer className="mr-4 text-orange-500" />
                Back-End
              </h3>
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                viewport={{ once: true }}
                className="inline-flex items-center bg-gradient-to-r from-orange-500 to-red-500 text-white px-6 py-3 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 group mx-auto sm:mx-0"
              >
                <FaCloud className="mr-3 text-lg group-hover:animate-pulse" />
                <span className="font-semibold text-lg">
                  Runs on Hedera Network
                </span>
                <motion.div
                  animate={{ rotate: [0, 360] }}
                  transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                  className="ml-3 w-2 h-2 bg-white rounded-full opacity-75"
                />
              </motion.div>
            </div>
            <div className="space-y-6 sm:space-y-8">
              {features.backend.map((feature, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  whileHover={{ scale: 1.02 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="bg-gradient-to-r from-orange-50 to-red-50 rounded-2xl p-6 group hover:shadow-lg transition-all duration-300"
                >
                  <div className="flex flex-col sm:flex-row items-center sm:items-start space-y-4 sm:space-y-0 sm:space-x-6">
                    <div className="flex-shrink-0 p-4 bg-orange-100 rounded-2xl text-orange-500 group-hover:bg-orange-200 transition-colors duration-300">
                      {React.cloneElement(feature.icon as React.ReactElement, {
                        className: "w-8 h-8",
                      })}
                    </div>
                    <div className="flex-1 text-center sm:text-left">
                      <h4 className="text-xl font-bold text-gray-800 mb-1">
                        {feature.title}
                      </h4>
                      <p className="text-sm font-medium text-orange-600 mb-3">
                        {feature.subtitle}
                      </p>
                      <p className="text-base text-gray-700 leading-relaxed mb-4">
                        {feature.description}
                      </p>

                      {/* Feature Details */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-4">
                        {feature.details.map((detail, detailIndex) => (
                          <div
                            key={detailIndex}
                            className="flex items-center text-sm text-gray-600"
                          >
                            <span className="mr-2">{detail}</span>
                          </div>
                        ))}
                      </div>

                      {/* Analogy */}
                      <div className="relative bg-white/60 rounded-xl p-4 pl-6 shadow-md">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 w-1.5 h-12 bg-gradient-to-b from-orange-400 to-red-400 rounded-full"></span>
                        <p className="text-sm text-gray-700 italic">
                          <span className="font-semibold text-orange-600">
                            Real-world analogy:
                          </span>{" "}
                          {feature.analogy}
                        </p>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Frontend Section */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="bg-white/70 backdrop-blur-sm rounded-3xl p-6 sm:p-10 shadow-xl hover:shadow-2xl transition-all duration-500"
          >
            <div className="mb-6 sm:mb-8">
              <h3 className="text-3xl sm:text-4xl font-bold text-gray-800 mb-4 flex items-center justify-center sm:justify-start">
                <FaDesktop className="mr-4 text-blue-500" />
                Front-End
              </h3>
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                viewport={{ once: true }}
                className="inline-flex items-center bg-gradient-to-r from-blue-500 to-cyan-500 text-white px-6 py-3 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 group mx-auto sm:mx-0"
              >
                <FaMobile className="mr-3 text-lg group-hover:animate-bounce" />
                <span className="font-semibold text-lg">
                  Runs on Your Device
                </span>
                <motion.div
                  animate={{ scale: [1, 1.2, 1] }}
                  transition={{
                    duration: 1.5,
                    repeat: Infinity,
                    ease: "easeInOut",
                  }}
                  className="ml-3 w-2 h-2 bg-white rounded-full opacity-75"
                />
              </motion.div>
            </div>
            <div className="space-y-6 sm:space-y-8">
              {features.frontend.map((feature, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  whileHover={{ scale: 1.02 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="bg-gradient-to-r from-blue-50 to-cyan-50 rounded-2xl p-6 group hover:shadow-lg transition-all duration-300"
                >
                  <div className="flex flex-col sm:flex-row items-center sm:items-start space-y-4 sm:space-y-0 sm:space-x-6">
                    <div className="flex-shrink-0 p-4 bg-blue-100 rounded-2xl text-blue-500 group-hover:bg-blue-200 transition-colors duration-300">
                      {React.cloneElement(feature.icon as React.ReactElement, {
                        className: "w-8 h-8",
                      })}
                    </div>
                    <div className="flex-1 text-center sm:text-left">
                      <h4 className="text-xl font-bold text-gray-800 mb-1">
                        {feature.title}
                      </h4>
                      <p className="text-sm font-medium text-blue-600 mb-3">
                        {feature.subtitle}
                      </p>
                      <p className="text-base text-gray-700 leading-relaxed mb-4">
                        {feature.description}
                      </p>

                      {/* Feature Details */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-4">
                        {feature.details.map((detail, detailIndex) => (
                          <div
                            key={detailIndex}
                            className="flex items-center text-sm text-gray-600"
                          >
                            <span className="mr-2">{detail}</span>
                          </div>
                        ))}
                      </div>

                      {/* Analogy */}
                      <div className="relative bg-white/60 rounded-xl p-4 pl-6 shadow-md">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 w-1.5 h-12 bg-gradient-to-b from-blue-400 to-cyan-400 rounded-full"></span>
                        <p className="text-sm text-gray-700 italic">
                          <span className="font-semibold text-blue-600">
                            Real-world analogy:
                          </span>{" "}
                          {feature.analogy}
                        </p>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default HowWorks;
